import { reportBrowsingDuration } from '@/api/browsingDuration';
import { BROWSE_MODULE, PAGE_TO_MODULE_MAP, HEARTBEAT_INTERVAL, BROWSING_CACHE_KEY } from '@/utils/constants';
import { getToken } from '@/utils/auth';
import { isExcludedUrl } from '@/utils/common';

/**
 * 浏览时长追踪器
 * 采用心跳增量上报机制，每2秒上报一次
 */
class BrowsingDurationTracker {
  constructor() {
    // 当前状态
    this.currentModule = null;
    this.currentUrl = '';
    this.isActive = true;
    
    // 计数器
    this.activeDelta = 0;       // 活跃时长累计（秒）
    this.loadingCount = 0;      // 滚动加载次数
    
    // 定时器
    this.heartbeatTimer = null;
    this.activeTimer = null;
    
    // 是否已初始化
    this.initialized = false;
    
    // 上一次活跃时间
    this.lastActiveTime = Date.now();
    
    // 使用公共的 URL 排除列表
    
    // 绑定事件处理函数
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
  }

  /**
   * 检查当前URL是否在排除列表中
   * @param {string} url - 要检查的URL
   * @returns {boolean}
   */
  isExcludedUrl(url) {
    return isExcludedUrl(url);
  }

  /**
   * 初始化追踪器
   */
  init() {
    if (this.initialized) return;
    
    // 检查登录状态
    // if (!getToken()) {
    //   console.log('[BrowsingTracker] 用户未登录，跳过初始化');
    //   return;
    // }
    
    // 从缓存恢复数据
    this.restoreFromCache();
    
    // 绑定事件
    this.bindEvents();
    
    // 启动心跳
    this.startHeartbeat();
    
    this.initialized = true;
    console.log('[BrowsingTracker] 初始化完成');
  }

  /**
   * 根据路由获取浏览模块
   * @param {string} path - 路由路径
   * @returns {number|null}
   */
  getBrowseModule(path) {
    // 处理 /wiki* 通配符匹配
    if (path.startsWith('/wiki')) {
      return BROWSE_MODULE.DOCUMENT;
    }
    // 处理 /forum 及其子路由
    if (path.startsWith('/forum')) {
      return BROWSE_MODULE.COMMUNITY;
    }
    // 精确匹配
    for (const [pattern, module] of Object.entries(PAGE_TO_MODULE_MAP)) {
      if (path === pattern || path.startsWith(pattern + '/')) {
        return module;
      }
    }
    return null;
  }

  /**
   * 切换页面模块
   * @param {string} path - 新的页面路径
   */
  switchPage(path,fullPath) {
    const newModule = this.getBrowseModule(path);
    
    // 如果模块变化或首次进入，上报旧数据并重置
    if (this.currentModule !== null && this.currentModule !== newModule) {
      // 立即上报当前模块的累计数据
      this.report(true);
    }
    
    // 更新当前模块
    this.currentModule = newModule;
    this.currentUrl = fullPath;
    
    // 重置计数器
    this.activeDelta = 0;
    this.loadingCount = 0;
    
    // 如果是目标页面，开始计时
    if (newModule !== null) {
      this.isActive = true;
      this.lastActiveTime = Date.now();
      console.log(`[BrowsingTracker] 进入模块: ${newModule}, URL: ${this.currentUrl}`);
    }
  }

  /**
   * 从缓存恢复数据
   */
  restoreFromCache() {
    try {
      const cached = sessionStorage.getItem(BROWSING_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        this.activeDelta = data.activeDelta || 0;
        this.loadingCount = data.loadingCount || 0;
        this.currentModule = data.currentModule;
        this.currentUrl = data.currentUrl || '';
        console.log('[BrowsingTracker] 从缓存恢复数据:', data);
        sessionStorage.removeItem(BROWSING_CACHE_KEY);
      }
    } catch (e) {
      console.error('[BrowsingTracker] 恢复缓存失败:', e);
    }
  }

  /**
   * 保存数据到缓存
   */
  saveToCache() {
    try {
      const data = {
        activeDelta: this.activeDelta,
        loadingCount: this.loadingCount,
        currentModule: this.currentModule,
        currentUrl: this.currentUrl
      };
      sessionStorage.setItem(BROWSING_CACHE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('[BrowsingTracker] 保存缓存失败:', e);
    }
  }

  /**
   * 启动心跳上报
   */
  startHeartbeat() {
    // 清除旧定时器
    this.stopHeartbeat();
    
    // 心跳定时器
    this.heartbeatTimer = setInterval(() => {
      this.report();
    }, HEARTBEAT_INTERVAL);
    
    // 活跃状态计时器（每秒检查一次）
    this.activeTimer = setInterval(() => {
      if (this.isActive && this.currentModule !== null) {
        const now = Date.now();
        const elapsed = Math.floor((now - this.lastActiveTime) / 1000);
        // 只在实际有变化时累加
        if (elapsed >= 1) {
          this.activeDelta += 1;
          this.lastActiveTime = now;
        }
      }
    }, 1000);
  }

  /**
   * 停止心跳
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.activeTimer) {
      clearInterval(this.activeTimer);
      this.activeTimer = null;
    }
  }

  /**
   * 绑定事件
   */
  bindEvents() {
    // 页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // 全屏变化
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    
    // 滚动事件（用于滚动加载计数）
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    // 页面关闭前保存数据
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * 解绑事件
   */
  unbindEvents() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * 处理页面可见性变化
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // 页面隐藏，暂停计时
      this.isActive = false;
      console.log('[BrowsingTracker] 页面隐藏，暂停计时');
    } else {
      // 页面显示，恢复计时
      this.isActive = true;
      this.lastActiveTime = Date.now();
      console.log('[BrowsingTracker] 页面显示，恢复计时');
    }
  }

  /**
   * 处理全屏变化
   */
  handleFullscreenChange() {
    // 全屏状态下继续计时（如视频全屏播放）
    const isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
    console.log('[BrowsingTracker] 全屏状态变化:', isFullscreen);
    // 全屏不影响计时，继续正常计时
  }

  /**
   * 处理滚动事件（节流）
   */
  handleScroll() {
    // 这里预留滚动加载计数的位置
    // loading_count 的具体触发位置待后续确定
    // 可以通过调用 incrementLoadingCount() 方法来手动增加计数
  }

  /**
   * 页面关闭前处理
   */
  handleBeforeUnload() {
    // 保存当前数据到缓存
    this.saveToCache();
  }

  /**
   * 增加滚动加载计数
   * 由外部调用，在滚动加载触发时调用此方法
   */
  incrementLoadingCount() {
    this.loadingCount++;
    console.log('[BrowsingTracker] 滚动加载计数:', this.loadingCount);
  }

  /**
   * 上报浏览时长
   * @param {boolean} immediate - 是否立即上报（不等待心跳）
   */
  async report(immediate = false) {
    // 检查当前URL是否在排除列表中
    if (this.isExcludedUrl(this.currentUrl)) {
      console.log('[BrowsingTracker] 当前URL在排除列表中，跳过上报:', this.currentUrl);
      return;
    }
    
    // 未登录状态下，禁止特定页面的上报
    if (!getToken()) {
      const path = this.currentUrl.replace(window.location.origin + '/#', '');
      if (path.includes('/assistant') || path.includes('/forum/search')) {
        console.log('[BrowsingTracker] 未登录状态，禁止上报:', path);
        return;
      }
    }
    
    // 检查是否有数据需要上报
    if (this.currentModule === null) return;
    if (this.activeDelta === 0 && this.loadingCount === 0 && !immediate) return;
    
    const data = {
      browse_module: this.currentModule,
      browse_url: this.currentUrl,
      active_delta: this.activeDelta,
      loading_count: this.loadingCount
    };
    
    console.log('[BrowsingTracker] 上报数据:', data);
    
    try {
      const res = await reportBrowsingDuration(data);
      if (res.code === 200 || res.ret === 200) {
        // 上报成功，重置计数器
        this.activeDelta = 0;
        this.loadingCount = 0;
        console.log('[BrowsingTracker] 上报成功，计数器已重置');
      } else {
        // 上报失败，计数器继续累加
        console.warn('[BrowsingTracker] 上报失败，计数器继续累加:', res.message || res.msg);
      }
    } catch (error) {
      // 网络错误等异常，计数器继续累加
      console.error('[BrowsingTracker] 上报异常:', error);
    }
  }

  /**
   * 销毁追踪器
   */
  destroy() {
    this.stopHeartbeat();
    this.unbindEvents();
    this.initialized = false;
    console.log('[BrowsingTracker] 已销毁');
  }
}

// 单例模式
let trackerInstance = null;

/**
 * 获取追踪器实例
 * @returns {BrowsingDurationTracker}
 */
export function getBrowsingTracker() {
  if (!trackerInstance) {
    trackerInstance = new BrowsingDurationTracker();
  }
  return trackerInstance;
}

export default BrowsingDurationTracker;
